/**
 * printUtils.js
 * Utility functions for generating printable HTML windows.
 * Extracted from AdminDashboard — keep pure (no React, no Supabase).
 */

// ─── Schedule Poster ──────────────────────────────────────────────────────────

export const generateSchedulePoster = (dateKey, dayGames, activeSeason) => {
  if (!dayGames || dayGames.length === 0) {
    alert("No games on this day to generate a poster for.");
    return;
  }

  const seasonStart = new Date(activeSeason.start_date);
  const gameDate = new Date(dateKey);
  const diffDays = Math.ceil(
    Math.abs(gameDate - seasonStart) / (1000 * 60 * 60 * 24),
  );
  const weekNum = Math.max(1, Math.ceil(diffDays / 7));

  const formattedDate = gameDate
    .toLocaleDateString("en-US", { month: "short", day: "numeric" })
    .toUpperCase();

  const weekTitle = "WEEK " + weekNum + " - " + formattedDate;

  const gamesHTML = dayGames
    .map(function (game) {
      const time = new Date(game.game_date).toLocaleTimeString("en-US", {
        hour: "numeric",
        minute: "2-digit",
      });
      return (
        '<div class="game-block">' +
        '<div class="time">' +
        time +
        "</div>" +
        '<div class="matchup">' +
        '<div class="team">' +
        (game.team1 && game.team1.logo_url
          ? '<img src="' +
            game.team1.logo_url +
            '" alt="' +
            (game.team1.name || "") +
            '" />'
          : "\uD83C\uDFC0") +
        "<span>" +
        (game.team1 && game.team1.name ? game.team1.name : "") +
        "</span>" +
        "</div>" +
        '<div class="vs">VS</div>' +
        '<div class="team">' +
        (game.team2 && game.team2.logo_url
          ? '<img src="' +
            game.team2.logo_url +
            '" alt="' +
            (game.team2.name || "") +
            '" />'
          : "\uD83C\uDFC0") +
        "<span>" +
        (game.team2 && game.team2.name ? game.team2.name : "") +
        "</span>" +
        "</div>" +
        "</div>" +
        "</div>"
      );
    })
    .join("");

  const html =
    "<!DOCTYPE html>" +
    '<html lang="en"><head>' +
    '<meta charset="UTF-8">' +
    "<title>Muqalab Hoops \u2014 " +
    weekTitle +
    " Schedule</title>" +
    "<style>" +
    "@import url('https://fonts.googleapis.com/css2?family=Bebas+Neue:wght@400;700&family=Impact&display=swap');" +
    "body{font-family:'Bebas Neue',sans-serif;background:#111;color:white;margin:0;padding:20px}" +
    ".poster{max-width:1200px;margin:0 auto;background:#111;border:12px solid #fff;border-radius:8px;overflow:hidden}" +
    ".header{background:linear-gradient(90deg,#6b21a8,#a855f7);padding:20px;text-align:center}" +
    ".header h1{font-size:3.5rem;letter-spacing:4px;margin:0}" +
    ".header h2{font-size:1.8rem;margin:4px 0 0;opacity:.9}" +
    ".games{padding:40px;background:#111}" +
    ".game-block{margin-bottom:48px;display:flex;align-items:center;gap:30px}" +
    ".time{font-size:2.5rem;font-weight:700;width:160px}" +
    ".matchup{display:flex;align-items:center;gap:30px;flex:1}" +
    ".team{display:flex;flex-direction:column;align-items:center;gap:8px}" +
    ".team img{width:110px;height:110px;object-fit:contain;border:6px solid #fff;border-radius:16px}" +
    ".vs{font-size:2.5rem;font-family:Impact,sans-serif;opacity:.8}" +
    ".reminders{position:absolute;top:220px;right:240px;width:320px;background:#fff;color:#111;border-radius:12px;padding:20px;box-shadow:0 10px 30px rgba(0,0,0,.6)}" +
    ".reminders h3{font-size:1.4rem;margin-bottom:12px;color:#6b21a8}" +
    ".clipboard{border:3px solid #6b21a8;border-radius:8px;padding:12px}" +
    ".footer{background:#1a1a1a;padding:20px;text-align:center;font-size:1.1rem;display:flex;justify-content:space-between;align-items:center}" +
    "</style></head><body>" +
    '<div class="poster">' +
    '<div class="header"><h1>' +
    weekTitle +
    "</h1><h2>SCHEDULE</h2></div>" +
    '<div class="games">' +
    gamesHTML +
    "</div>" +
    '<div class="reminders"><div class="clipboard"><h3>REMINDERS</h3>' +
    '<div style="font-size:.85rem">' +
    "<div>7:50 PM \u2014 MAGHRIB</div>" +
    "<div>8:00 PM \u2014 REMINDER 1</div>" +
    "<div>8:05 PM \u2014 TIP OFF</div>" +
    "<div>9:00 PM \u2014 REMINDER 2</div>" +
    "<div>9:05 PM \u2014 TIP OFF</div>" +
    "<div>10:00 PM \u2014 ISHA</div>" +
    '<div style="padding-top:12px;border-top:1px solid #ccc;color:red;font-weight:700">NO CURSING / REVEALING AWRAH</div>' +
    "</div></div></div>" +
    '<div class="footer">' +
    "<div><strong>MUQALAB HOOPS</strong><br>SEASON 3</div>" +
    "<div>\uD83D\uDCCD T3 PERFORMANCE, AVON<br>1945, 1965 PERFORMANCE LN</div>" +
    "<div>muqalabhoops.org</div>" +
    "</div>" +
    "</div>" +
    "<script>window.onload=function(){setTimeout(function(){window.print();},600);};</script>" +
    "</body></html>";

  const win = window.open("", "_blank");
  win.document.write(html);
  win.document.close();
};

// ─── Score Sheet ──────────────────────────────────────────────────────────────

/**
 * printScoreSheet
 *
 * Portrait, two-page back-to-back score sheet:
 *   Page 1 — 1st Half: compact header, Team 1 table stacked above Team 2 table
 *   Page 2 — 2nd Half: same layout + Final Score + Signatures + OT section
 *
 * Design:
 *  - Both teams stack vertically on one portrait page per half
 *  - Timeouts (2 per half) shown inline in each team's header row as circles
 *  - Team fouls tracked per-half only — one compact row above each team table
 *  - No dedicated timeout section, no carryover note, no backup rows
 *  - Tight margins/fonts to maximise row count on portrait paper
 *
 * @param {object} game      - game row with team1/team2 joins (id, name, captain_id, logo_url)
 * @param {Array}  t1players - players for team 1
 * @param {Array}  t2players - players for team 2
 */
export const printScoreSheet = function (game, t1players, t2players) {
  // ── Game metadata ────────────────────────────────────────────────────────
  var t1name = (game.team1 && game.team1.name) || "Team 1";
  var t2name = (game.team2 && game.team2.name) || "Team 2";
  var t1captainId = game.team1 && game.team1.captain_id;
  var t2captainId = game.team2 && game.team2.captain_id;

  var p0 = (game.game_date || "").split("T");
  var dp = (p0[0] || "").split("-").map(Number);
  var tp = (p0[1] || "00:00").split(":").map(Number);
  var localDate = new Date(dp[0], dp[1] - 1, dp[2], tp[0], tp[1]);
  var gameDate = localDate.toLocaleDateString("en-US", {
    weekday: "long",
    year: "numeric",
    month: "long",
    day: "numeric",
  });
  var gameTime = localDate.toLocaleTimeString("en-US", {
    hour: "numeric",
    minute: "2-digit",
  });
  var court = game.court || "\u2014";

  // ── CSS ──────────────────────────────────────────────────────────────────
  var css = [
    "@import url('https://fonts.googleapis.com/css2?family=Bebas+Neue&family=IBM+Plex+Mono:wght@400;600&family=IBM+Plex+Sans:wght@400;600;700&display=swap');",
    "*,*::before,*::after{box-sizing:border-box;margin:0;padding:0}",
    // Portrait page, tight body padding
    "body{font-family:'IBM Plex Sans',sans-serif;background:#fff;color:#000;font-size:10px}",
    ".page{padding:8px 10px}",
    ".page-break{page-break-after:always;break-after:page}",
    // Compact page header
    ".pg-header{display:flex;align-items:flex-start;justify-content:space-between;border-bottom:3px solid #000;padding-bottom:5px;margin-bottom:6px}",
    ".league-name{font-family:'Bebas Neue',sans-serif;font-size:22px;letter-spacing:2px;line-height:1}",
    ".league-sub{font-size:8px;font-weight:600;letter-spacing:1.5px;text-transform:uppercase;color:#555;margin-top:2px}",
    ".game-meta{text-align:right;font-family:'IBM Plex Mono',monospace;font-size:8.5px;line-height:1.55}",
    ".game-meta strong{font-weight:600}",
    // Matchup bar
    ".matchup{display:flex;align-items:center;justify-content:center;gap:10px;background:#000;color:#fff;padding:5px 14px;margin-bottom:5px;border-radius:2px}",
    ".matchup-team{font-family:'Bebas Neue',sans-serif;font-size:20px;letter-spacing:2px;flex:1;text-align:center}",
    ".matchup-vs{font-family:'IBM Plex Mono',monospace;font-size:10px;font-weight:600;color:#888;flex-shrink:0}",
    // Legend
    ".legend{font-size:7px;font-family:'IBM Plex Mono',monospace;color:#666;margin-bottom:5px;text-align:right}",
    ".legend span{color:#b8860b}",
    // Team block (full-width, stacked)
    ".team-block{border:1.5px solid #000;border-radius:2px;overflow:hidden;margin-bottom:6px}",
    // Team header row: name left, fouls centre, timeouts right
    ".team-hdr{display:flex;align-items:center;background:#111;color:#fff;padding:3px 7px;gap:8px}",
    ".team-hdr-name{font-family:'Bebas Neue',sans-serif;font-size:13px;letter-spacing:1.5px;flex:1}",
    ".team-hdr-fouls{font-family:'IBM Plex Mono',monospace;font-size:7.5px;display:flex;align-items:center;gap:3px;flex-shrink:0}",
    ".foul-cells{display:flex;gap:2px}",
    ".fc{width:14px;height:14px;border:1px solid #666;border-radius:1px;display:inline-block}",
    ".fc.bonus{border-color:#f57c00;background:rgba(245,124,0,.15)}",
    ".fc.dbl{border-color:#c62828;background:rgba(198,40,40,.12)}",
    ".foul-lbl{font-size:6.5px;color:#aaa;margin-right:2px}",
    ".team-hdr-to{font-family:'IBM Plex Mono',monospace;font-size:7.5px;display:flex;align-items:center;gap:3px;flex-shrink:0}",
    ".to-lbl{font-size:6.5px;color:#aaa}",
    ".to-circle{width:13px;height:13px;border-radius:50%;border:1.5px solid #aaa;display:inline-block}",
    // Stats table
    "table{width:100%;border-collapse:collapse;font-family:'IBM Plex Mono',monospace;font-size:8.5px}",
    "table{table-layout:fixed}",
    "thead th{background:#f0f0f0;border-bottom:1.5px solid #000;border-right:1px solid #ccc;padding:2px 1px;text-align:center;font-weight:700;font-size:7.5px;letter-spacing:.3px}",
    "thead th:last-child{border-right:none}",
    "tbody td{border-bottom:1px solid #e8e8e8;border-right:1px solid #ebebeb;height:18px;padding:0 2px;vertical-align:middle}",
    "tbody td:last-child{border-right:none}",
    // Column widths — stat cols wide for tally marks (||||/ ||||/), name col narrow
    ".c-num{width:16px;text-align:center;font-weight:700}",
    ".c-name{width:50px;max-width:50px;overflow:hidden;white-space:nowrap}",
    ".c-stat{width:52px;text-align:left;padding-left:3px!important}",
    ".c-pf{width:16px;text-align:center;background:#fff8f0}",
    ".c-tf{width:16px;text-align:center;background:#fff0f0}",
    ".c-pts{width:24px;text-align:center;background:#f0f0f0;font-weight:700;border-left:1.5px solid #bbb!important}",
    ".tally{background-image:repeating-linear-gradient(90deg,transparent,transparent 4px,#efefef 4px,#efefef 5px)}",
    ".player-last{font-weight:600;font-size:8.5px}",
    ".player-first{font-size:7px;color:#666;margin-left:2px}",
    ".captain-star{color:#b8860b}",
    // Half-score summary row
    ".score-row td{background:#222;color:#fff;height:18px;font-weight:700;font-size:7.5px}",
    ".score-lbl{text-align:center;letter-spacing:.8px;text-transform:uppercase;font-family:'IBM Plex Mono',monospace}",
    ".score-box{border-left:2px solid #555!important}",
    // Final score block
    ".final-score{border:2px solid #000;border-radius:2px;margin-bottom:7px;overflow:hidden}",
    ".final-score-title{background:#000;color:#fff;font-family:'Bebas Neue',sans-serif;font-size:12px;letter-spacing:2px;padding:3px 8px}",
    ".final-score-body{display:grid;grid-template-columns:1fr 40px 1fr}",
    ".final-team{padding:6px 8px}",
    ".final-team-name{font-weight:700;font-size:9px;letter-spacing:.5px;text-transform:uppercase;margin-bottom:4px}",
    ".final-score-lines{display:grid;grid-template-columns:1fr 1fr 1fr;gap:3px}",
    ".sl-label{font-size:6.5px;color:#666;text-align:center;text-transform:uppercase;letter-spacing:.3px;margin-bottom:2px}",
    ".sl-box{height:24px;border:1.5px solid #000;border-radius:1px}",
    ".sl-total{border-width:2.5px}",
    ".final-divider{border-left:2px solid #000;border-right:2px solid #000;display:flex;align-items:center;justify-content:center;font-family:'Bebas Neue',sans-serif;font-size:16px;color:#bbb}",
    // Signatures
    ".sigs{display:grid;grid-template-columns:1fr 1fr 1fr;gap:14px;margin-top:5px;margin-bottom:6px}",
    ".sig-block{border-top:1.5px solid #000;padding-top:4px}",
    ".sig-label{font-size:7px;font-weight:600;letter-spacing:.8px;text-transform:uppercase;color:#555}",
    // Footer
    ".footer{display:flex;justify-content:space-between;align-items:center;padding-top:5px;border-top:1px solid #ccc;font-size:6.5px;color:#999;font-family:'IBM Plex Mono',monospace}",
    // OT section (on page 2, below signatures)
    ".ot-section{border-top:2px dashed #999;margin-top:8px;padding-top:6px}",
    ".ot-banner{font-family:'Bebas Neue',sans-serif;font-size:14px;letter-spacing:2px;background:#1a1a1a;color:#fff;padding:3px 8px;border-radius:2px;margin-bottom:5px;display:inline-block}",
    ".ot-note{font-size:7px;font-family:'IBM Plex Mono',monospace;color:#666;margin-bottom:5px}",
    ".ot-teams{display:grid;grid-template-columns:1fr 1fr;gap:8px;margin-bottom:6px}",
    ".ot-team{border:1.5px solid #000;border-radius:2px;overflow:hidden}",
    ".ot-team-hdr{background:#333;color:#fff;font-family:'Bebas Neue',sans-serif;font-size:11px;letter-spacing:1px;padding:2px 6px;display:flex;justify-content:space-between;align-items:center}",
    ".ot-to{font-size:7px;font-family:'IBM Plex Mono',monospace;color:#ccc;display:flex;align-items:center;gap:3px}",
    ".ot-score-box{height:32px;border:1.5px solid #000;border-radius:1px;margin:5px 6px}",
    ".ot-result{border:1.5px solid #000;border-radius:2px;overflow:hidden;margin-bottom:5px}",
    ".ot-result-title{background:#000;color:#fff;font-family:'Bebas Neue',sans-serif;font-size:11px;letter-spacing:1.5px;padding:2px 7px}",
    ".ot-result-body{display:grid;grid-template-columns:1fr 36px 1fr}",
    // Print
    "@media print{body{padding:0}@page{size:portrait;margin:5mm}.page-break{page-break-after:always;break-after:page}}",
  ].join("\n");

  // ── Foul cells for team header (10 cells, bonus/dbl markers) ─────────────
  function foulCellsHtml() {
    var out = "";
    for (var n = 1; n <= 10; n++) {
      var cls = n === 7 ? "fc bonus" : n === 10 ? "fc dbl" : "fc";
      out += '<span class="' + cls + '" title="' + n + '"></span>';
    }
    return out;
  }

  // ── Timeout circles (2 per half) ─────────────────────────────────────────
  function timeoutCircles(count) {
    var out = "";
    for (var i = 0; i < count; i++) {
      out += '<span class="to-circle"></span>';
    }
    return out;
  }

  // ── Team header row (name + foul tracker + timeout circles) ──────────────
  function teamHdr(teamName, toCount) {
    return (
      '<div class="team-hdr">' +
      '<span class="team-hdr-name">' +
      teamName +
      "</span>" +
      '<span class="team-hdr-fouls">' +
      '<span class="foul-lbl">FOULS</span>' +
      '<span class="foul-cells">' +
      foulCellsHtml() +
      "</span>" +
      "</span>" +
      '<span class="team-hdr-to">' +
      '<span class="to-lbl">TO</span>' +
      timeoutCircles(toCount) +
      "</span>" +
      "</div>"
    );
  }

  // ── Player rows ───────────────────────────────────────────────────────────
  // Pads to at least 10 rows. No backup rows added.
  function buildPlayerRows(players, captainId) {
    var rows = (players || []).map(function (p) {
      var isCap = String(p.id) === String(captainId);
      var parts = p.name.trim().split(" ");
      var last =
        parts.length > 1
          ? parts[parts.length - 1].toUpperCase()
          : p.name.toUpperCase();
      // First initial only — keeps name column narrow (e.g. "Ahmad" → "A.")
      var firstInitial =
        parts.length > 1 ? parts[0].charAt(0).toUpperCase() + "." : "";
      var star = isCap ? ' <span class="captain-star">\u2605</span>' : "";
      return (
        "<tr>" +
        '<td class="c-num">' +
        (p.jersey_number != null ? p.jersey_number : "") +
        "</td>" +
        '<td class="c-name"><span class="player-last">' +
        last +
        star +
        '</span><span class="player-first">' +
        firstInitial +
        "</span></td>" +
        '<td class="c-stat tally"></td>' +
        '<td class="c-stat tally"></td>' +
        '<td class="c-stat tally"></td>' +
        '<td class="c-stat tally"></td>' +
        '<td class="c-stat tally"></td>' +
        '<td class="c-pf"></td>' +
        '<td class="c-tf"></td>' +
        '<td class="c-pts"></td>' +
        "</tr>"
      );
    });

    var pad = Math.max(0, 10 - rows.length);
    for (var i = 0; i < pad; i++) {
      rows.push(
        "<tr>" +
          '<td class="c-num"></td><td class="c-name"></td>' +
          '<td class="c-stat tally"></td><td class="c-stat tally"></td>' +
          '<td class="c-stat tally"></td><td class="c-stat tally"></td>' +
          '<td class="c-stat tally"></td>' +
          '<td class="c-pf"></td><td class="c-tf"></td><td class="c-pts"></td>' +
          "</tr>",
      );
    }
    return rows.join("");
  }

  // ── Full team scoring table ───────────────────────────────────────────────
  function teamTable(teamName, players, captainId, toCount) {
    return (
      '<div class="team-block">' +
      teamHdr(teamName, toCount) +
      "<table><thead><tr>" +
      '<th class="c-num">#</th>' +
      '<th class="c-name">Player</th>' +
      '<th class="c-stat">1PT</th>' +
      '<th class="c-stat">2PT</th>' +
      '<th class="c-stat">3PT</th>' +
      '<th class="c-stat">REB</th>' +
      '<th class="c-stat">AST</th>' +
      '<th class="c-pf">PF</th>' +
      '<th class="c-tf">TF</th>' +
      '<th class="c-pts">PTS</th>' +
      "</tr></thead><tbody>" +
      buildPlayerRows(players, captainId) +
      '<tr class="score-row">' +
      '<td colspan="2" class="score-lbl">HALF SCORE</td>' +
      '<td colspan="8" class="score-box"></td>' +
      "</tr>" +
      "</tbody></table>" +
      "</div>"
    );
  }

  // ── Page header ───────────────────────────────────────────────────────────
  function pageHeader(halfLabel) {
    return (
      '<div class="pg-header">' +
      "<div>" +
      '<div class="league-name">Muqalab Hoops</div>' +
      '<div class="league-sub">Official Score Sheet \u2014 ' +
      halfLabel +
      "</div>" +
      "</div>" +
      '<div class="game-meta">' +
      "<div><strong>Date:</strong> " +
      gameDate +
      "</div>" +
      "<div><strong>Time:</strong> " +
      gameTime +
      "</div>" +
      "<div><strong>Court:</strong> " +
      court +
      "</div>" +
      "<div><strong>Scorekeeper:</strong> ___________________</div>" +
      "</div>" +
      "</div>" +
      '<div class="matchup">' +
      '<div class="matchup-team">' +
      t1name +
      "</div>" +
      '<div class="matchup-vs">VS</div>' +
      '<div class="matchup-team">' +
      t2name +
      "</div>" +
      "</div>" +
      '<div class="legend">' +
      "<span>\u2605</span> = Captain &nbsp;|&nbsp; " +
      '<span style="color:#c0392b">PF</span> = Personal Foul (DQ@6) &nbsp;|&nbsp; ' +
      '<span style="color:#7b0000">TF</span> = Technical Foul (DQ@2) &nbsp;|&nbsp; ' +
      "TO = Timeouts remaining" +
      "</div>"
    );
  }

  // ── Final score block ─────────────────────────────────────────────────────
  function scoreLineGroup(teamName) {
    return (
      '<div class="final-team">' +
      '<div class="final-team-name">' +
      teamName +
      "</div>" +
      '<div class="final-score-lines">' +
      '<div><div class="sl-label">1st Half</div><div class="sl-box"></div></div>' +
      '<div><div class="sl-label">2nd Half</div><div class="sl-box"></div></div>' +
      '<div><div class="sl-label">Total</div><div class="sl-box sl-total"></div></div>' +
      "</div>" +
      "</div>"
    );
  }

  var finalScoreBlock =
    '<div class="final-score">' +
    '<div class="final-score-title">Final Score</div>' +
    '<div class="final-score-body">' +
    scoreLineGroup(t1name) +
    '<div class="final-divider">\u2014</div>' +
    scoreLineGroup(t2name) +
    "</div>" +
    "</div>";

  // ── Signatures ────────────────────────────────────────────────────────────
  var sigsBlock =
    '<div class="sigs">' +
    '<div class="sig-block"><div class="sig-label">Scorekeeper Signature</div></div>' +
    '<div class="sig-block"><div class="sig-label">' +
    t1name +
    " Captain</div></div>" +
    '<div class="sig-block"><div class="sig-label">' +
    t2name +
    " Captain</div></div>" +
    "</div>";

  // ── Footer ────────────────────────────────────────────────────────────────
  var footerBlock =
    '<div class="footer">' +
    "<span>Muqalab Hoops \u2014 Season 3</span>" +
    "<span>" +
    t1name +
    " vs " +
    t2name +
    " \u00b7 " +
    gameDate +
    " \u00b7 " +
    gameTime +
    " \u00b7 " +
    court +
    "</span>" +
    "<span>muqalabhoops.org</span>" +
    "</div>";

  // ── OT section (appended to page 2) ──────────────────────────────────────
  // Printed automatically; scorekeeper ignores if game doesn't go to OT.
  // 1 timeout per team in OT, no carryover.
  var otSection =
    '<div class="ot-section">' +
    '<div class="ot-banner">\u23F1 Overtime</div>' +
    '<div class="ot-note">Use only if tied after 2nd half. Each team has 1 timeout in OT. No carryover.</div>' +
    '<div class="ot-teams">' +
    // Team 1 OT
    '<div class="ot-team">' +
    '<div class="ot-team-hdr">' +
    t1name +
    '<span class="ot-to"><span class="to-lbl">TO</span><span class="to-circle"></span></span>' +
    "</div>" +
    '<div class="ot-score-box"></div>' +
    "</div>" +
    // Team 2 OT
    '<div class="ot-team">' +
    '<div class="ot-team-hdr">' +
    t2name +
    '<span class="ot-to"><span class="to-lbl">TO</span><span class="to-circle"></span></span>' +
    "</div>" +
    '<div class="ot-score-box"></div>' +
    "</div>" +
    "</div>" +
    // OT result
    '<div class="ot-result">' +
    '<div class="ot-result-title">OT Result</div>' +
    '<div class="ot-result-body">' +
    '<div class="final-team"><div class="final-team-name">' +
    t1name +
    "</div>" +
    '<div class="final-score-lines">' +
    '<div><div class="sl-label">OT Score</div><div class="sl-box"></div></div>' +
    '<div><div class="sl-label">Final Total</div><div class="sl-box sl-total"></div></div>' +
    "</div></div>" +
    '<div class="final-divider">\u2014</div>' +
    '<div class="final-team"><div class="final-team-name">' +
    t2name +
    "</div>" +
    '<div class="final-score-lines">' +
    '<div><div class="sl-label">OT Score</div><div class="sl-box"></div></div>' +
    '<div><div class="sl-label">Final Total</div><div class="sl-box sl-total"></div></div>' +
    "</div></div>" +
    "</div></div>" +
    "</div>";

  // ── Assemble pages ────────────────────────────────────────────────────────
  // Page 1: 1st Half — Team 1 stacked above Team 2, portrait
  var page1 =
    '<div class="page page-break">' +
    pageHeader("1st Half") +
    teamTable(t1name, t1players, t1captainId, 2) +
    teamTable(t2name, t2players, t2captainId, 2) +
    footerBlock +
    "</div>";

  // Page 2: 2nd Half — same layout + Final Score + Sigs + OT section
  var page2 =
    '<div class="page">' +
    pageHeader("2nd Half") +
    teamTable(t1name, t1players, t1captainId, 2) +
    teamTable(t2name, t2players, t2captainId, 2) +
    finalScoreBlock +
    sigsBlock +
    otSection +
    footerBlock +
    "</div>";

  var html =
    "<!DOCTYPE html>" +
    '<html lang="en"><head>' +
    '<meta charset="UTF-8">' +
    "<title>Score Sheet \u2014 " +
    t1name +
    " vs " +
    t2name +
    "</title>" +
    "<style>" +
    css +
    "</style>" +
    "</head><body>" +
    page1 +
    page2 +
    "<script>window.addEventListener('load',function(){setTimeout(function(){window.print();},400);});</script>" +
    "</body></html>";

  var win = window.open("", "_blank");
  win.document.write(html);
  win.document.close();
};
